import { useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useLocation, useNavigate } from "react-router-dom";
import {
  empty,
  errorResponse,
  isArray,
  isString,
  prepareResponseData,
} from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import schoolApi from "../../api/School";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import TableLoading from "../../components/skeleton/TableLoading";
import { FaPlus } from "react-icons/fa";
import UserGroupTable from "../../components/tables/primeTable/userGroups/UserGroupTable";

const UserGroups = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  //ref
  const toastTR = useRef(null);
  // states
  const [userGroups, setUserGroups] = useState([]);
  const [userGroupModalVisibility, setUserGroupModalVisibility] =
    useState(false);
  const [selectedUserGroupDetail, setSelectedUserGroupDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [modalType, setModalType] = useState("update");

  useEffect(() => {
    if (!empty(location) && !empty(location.state)) {
      const paramState = !empty(location.state) ? location.state : {};
      let userGroupAdded = !empty(paramState.userGroupAdded)
        ? paramState.userGroupAdded
        : false;
      let userGroupUpdated = !empty(paramState.userGroupUpdated)
        ? paramState.userGroupUpdated
        : false;
      if (userGroupAdded === true || userGroupUpdated === true) {
        const actionType = userGroupAdded
          ? "added"
          : userGroupUpdated
          ? "updated"
          : "";
        paramState.userGroupAdded = false;
        paramState.userGroupUpdated = false;
        responseDialog(
          "success",
          "Success",
          `User Group ${actionType} successfully!`
        );
        if (!empty(location) && !empty(location.state)) {
          navigate(null, {
            replace: true,
            state: {
              userGroupAdded: false,
              userGroupUpdated: false,
            },
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    // fetch user groups
    getUserGroups();
  }, []);

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  // function to get all user groups
  const getUserGroups = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await schoolApi.getUserGroups(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          `Failed to fetch user groups.`
        );
      }

      setUserGroups(
        isArray(response_data.response) ? response_data.response : []
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  //function to toggle user group visibility
  const toggleUserGroupVisibility = async () => {
    try {
      if (userGroupModalVisibility) setUserGroupModalVisibility(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const active =
        !empty(selectedUserGroupDetail) &&
        !empty(selectedUserGroupDetail.active) &&
        isString(selectedUserGroupDetail.active) &&
        selectedUserGroupDetail.active.toLowerCase() === "yes"
          ? "No"
          : "Yes";
      const userGroupId =
        !empty(selectedUserGroupDetail) && !empty(selectedUserGroupDetail._id)
          ? selectedUserGroupDetail._id
          : "";
      const response = await schoolApi.toggleUserGroupVisibility(
        schoolId,
        userGroupId,
        active
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to ${
                active === "Yes" ? "enable" : "disable"
              } user group with title, ${
                !empty(selectedUserGroupDetail.title)
                  ? selectedUserGroupDetail.title
                  : ""
              }`;
        return responseDialog("error", "Something went wrong", error_response);
      }

      responseDialog(
        "success",
        "Success",
        `User Group, ${
          !empty(selectedUserGroupDetail.title)
            ? selectedUserGroupDetail.title
            : ""
        }, was ${active === "Yes" ? "disabled" : "enabled"} successfully!`
      );
      return getUserGroups();
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  // delete user group
  const deleteUserGroup = async () => {
    try {
      if (userGroupModalVisibility) setUserGroupModalVisibility(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const userGroupId =
        !empty(selectedUserGroupDetail) && !empty(selectedUserGroupDetail._id)
          ? selectedUserGroupDetail._id
          : "";
      const response = await schoolApi.deleteUserGroup(schoolId, userGroupId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to delete user group";
        return responseDialog("error", "Something went wrong", error_response);
      }

      responseDialog(
        "success",
        "Success",
        `User Group, ${
          !empty(selectedUserGroupDetail.title)
            ? selectedUserGroupDetail.title
            : ""
        }, was deleted successfully!`
      );
      return getUserGroups();
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  //function to show confirm modal
  const showDisableConfirm = (data) => {
    setModalType("update");
    setSelectedUserGroupDetail(data);
    setUserGroupModalVisibility(true);
  };

  //function to hide confirm modal
  const hideUserGroupModal = () => {
    setUserGroupModalVisibility(false);
  };

  //go to update user group page
  const gotToUpdateUserGroup = (data) => {
    const userGroupId = !empty(data) && !empty(data._id) ? data._id : "";
    navigate(`/user-group/${userGroupId}/update`);
  };

  // delete user group
  const showDeleteuserGroupModal = (data) => {
    setModalType("delete");
    setSelectedUserGroupDetail(data);
    setUserGroupModalVisibility(true);
  };

  const userGroupModalFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: "#e65061",
          borderColor: "#e65061",
          borderWidth: 1,
        }}
        onClick={hideUserGroupModal}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: "#389d17",
          borderColor: "#389d17",
          borderWidth: 1,
        }}
        onClick={() =>
          modalType === "update"
            ? toggleUserGroupVisibility()
            : deleteUserGroup()
        }
      />
    </div>
  );

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader
              button
              buttonText="Add User Groups"
              icon={<FaPlus />}
              iconMarginRight={8}
              link="/user-group/add"
              title="User Groups"
            />
            {/* end of header */}

            {/* table start */}
            {!isLoading ? (
              <UserGroupTable
                userGroups={userGroups}
                confirmDisableSelected={showDisableConfirm}
                goToUpdateUserGroupScreen={gotToUpdateUserGroup}
                goToDeleteUserGroupScreen={showDeleteuserGroupModal}
              />
            ) : (
              <TableLoading rows={20} cols={3} header={false} />
            )}
            {/* table end  */}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Dialog
        visible={userGroupModalVisibility}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={userGroupModalFooter}
        onHide={hideUserGroupModal}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <span>
              Are you sure you want to{" "}
              {modalType === "update" ? (
                !empty(selectedUserGroupDetail) &&
                !empty(selectedUserGroupDetail.active) &&
                isString(selectedUserGroupDetail.active) &&
                selectedUserGroupDetail.active.toLowerCase() === "yes" ? (
                  <span style={{ color: "red" }}>
                    <strong>disable</strong>
                  </span>
                ) : (
                  <span style={{ color: "green" }}>
                    <strong>enable</strong>
                  </span>
                )
              ) : (
                <span style={{ color: "red" }}>
                  <strong>delete</strong>
                </span>
              )}{" "}
              the selected user group -{" "}
              <strong>
                {!empty(selectedUserGroupDetail) &&
                !empty(selectedUserGroupDetail.title)
                  ? selectedUserGroupDetail.title
                  : ""}
              </strong>
              ?
            </span>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default UserGroups;
